import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { DataTablePFSEvent } from "primereact/datatable";
import { useCallback, useState, useEffect } from "react";
import { DCSQueryClient } from "../../../queryClient";
import { If } from "../../../components/If/If";
import { useFilterQuery } from "../../../hooks/useFilterQuery";
import { useToast } from "../../../Providers/ToastProvider";
import { requests } from "../../../services/http-common";
import { IFilter } from "../../../types";
import { DeleteItemPopup } from "../components/DeleteItemPopup/DeleteItemPopup";

import classes from "./Inventory.module.scss";
import { IInventory } from "./Inventory.model";
import { TItem } from "../components/types/index";
import { InventoryList } from "./components/InventoryList/InventoryList";
import { SetInventory } from "./components/SetInventory/SetInventory";
import { PrivateAccess } from "../../../keycloak";
import { UserRoles } from "../../../constants/userRoles";

const defaultFilters = {
  first: 0,
  rows: 10,
  page: 1,
  filter: "",
  filterByStatus: "",
  filterByCustomerCategory: "",
  sortOrder: -1,
};

export const Inventory = () => {
  const [filters, setFilters] = useState<IFilter>(defaultFilters as IFilter);
  const [item, setItem] = useState<IInventory | null>(null);
  const [itemDialog, setItemDialog] = useState(false);
  const [deleteItemDialog, setDeleteItemDialog] = useState(false);

  const filterQuery = useFilterQuery(filters);
  const { show } = useToast();

  const { data, refetch } = useQuery({
    queryKey: ["inventory-items"],
    queryFn: () => {
      return requests.get(`/inventory?${filterQuery}`);
    },
  });

  useEffect(() => {
    refetch();
  }, [filterQuery, refetch]);

  const addItemRequest = useMutation({
    mutationFn: (newItem: IInventory) => {
      return requests.post("/inventory", newItem);
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item added successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["inventory-items"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const editItemRequest = useMutation({
    mutationFn: ({ id, name, number }: IInventory) => {
      return requests.put(`/inventory/${id}`, {
        name,
        number,
      });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item updated successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["inventory-items"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const deleteItem = useMutation({
    mutationFn: ({ id }: IInventory) => {
      return requests.delete(`/inventory/${id}`);
    },
    onSuccess: () => {
      setDeleteItemDialog(false);
      DCSQueryClient.invalidateQueries({
        queryKey: ["inventory-items"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const handleAddInventory = (inventory: IInventory) => {
    addItemRequest.mutate(inventory);
  };

  const handleUpdateInventory = (inventory: IInventory) => {
    editItemRequest.mutate(inventory);
  };

  const onPage = useCallback(
    (event: DataTablePFSEvent) => {
      setFilters((currVal: any) => ({
        ...currVal,
        first: event.first,
        rows: event.rows,
        page: event.page,
      }));
    },
    [setFilters]
  );

  const editItem = (inventory: IInventory) => {
    setItem(inventory);
    setItemDialog(true);
  };

  const confirmDeleteItem = (inventory: IInventory) => {
    setItem(inventory);
    setDeleteItemDialog(true);
  };

  const hideDeleteItemDialog = () => {
    setDeleteItemDialog(false);
  };

  const hideItemDialog = () => {
    setItemDialog(false);
    setItem(null);
  };

  const handleAddItem = () => {
    setItem({
      id: null,
      name: "",
      number: "",
    });
    setItemDialog(true);
  };

  const cardTitle = () => {
    return (
      <div className={classes.cardTitle}>
        <h4>Inventory</h4>
        <PrivateAccess
          roles={{
            dcs_client: [UserRoles.INVENTORY_CREATE],
          }}
        >
          <Button
            icon="pi pi-plus"
            className=""
            label="New"
            onClick={handleAddItem}
          />
        </PrivateAccess>
      </div>
    );
  };

  return (
    <div>
      <div className={classes.container}>
        <Card title={cardTitle}>
          <InventoryList
            totalRecords={data?.total}
            records={data?.result}
            first={filters.first}
            rows={filters.rows}
            onPage={onPage}
            rowActionDelete={confirmDeleteItem}
            rowActionEdit={editItem}
          />
        </Card>
        <If condition={deleteItemDialog}>
          <DeleteItemPopup
            item={item as TItem}
            onHide={hideDeleteItemDialog}
            onConfirm={deleteItem.mutate as (item: TItem) => void}
            deleteRequestPending={deleteItem.isLoading}
          />
        </If>
        <If condition={!!itemDialog}>
          <SetInventory
            item={item}
            onHide={hideItemDialog}
            onAdd={handleAddInventory}
            onUpdate={handleUpdateInventory}
            requestPending={
              addItemRequest.isLoading || editItemRequest.isLoading
            }
          />
        </If>
      </div>
    </div>
  );
};
